import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import WorkflowActionsBar from '../Workflow/WorkflowActionsBar';
import { WorkflowProvider } from 'components/Workflow/WorkflowContext';
import { formatDateInvoices } from '../../common/utils';
import { WorkflowStatusChip } from 'components/common/WorkflowStatusChip';
import InvoiceReconciliationDialog from './InvoiceReconciliationDialog';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from '../../common/errorHandler';

const InvoicingList = ({
  searchResult,
  isValidating,
  loading,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
}) => {
  const navigate = useNavigate();

  const api: Api = useApi();
  const toast = useToastMessageQueue();

  const [reconciliationOpen, setReconciliationOpen] = useState(false);
  const [reconciliationRow, setReconciliationRow] = useState<any>(null);
  const pendingReconcileResolve = useRef<((result: boolean | 'handled') => void) | null>(null);
  const pendingReconcileStateId = useRef<number | null>(null);

  const handleBeforeAction = useCallback(
    (row: any) => (targetStateId: number, actionName?: string): Promise<boolean | 'handled'> => {
      if (actionName === 'Reconciliate') {
        setReconciliationRow(row);
        pendingReconcileStateId.current = targetStateId;
        setReconciliationOpen(true);
        return new Promise<boolean | 'handled'>((resolve) => {
          pendingReconcileResolve.current = resolve;
        });
      }
      return Promise.resolve(true);
    },
    []
  );

  const handleReconciliationConfirm = async () => {
    setReconciliationOpen(false);
    const targetStateId = pendingReconcileStateId.current;
    const row = reconciliationRow;
    pendingReconcileResolve.current?.('handled');
    pendingReconcileResolve.current = null;
    pendingReconcileStateId.current = null;

    if (targetStateId != null && row?.id) {
      try {
        await api.workflowApi.apiVversionWorkflowUpdateStatePost('1', {
          workflowStateId: targetStateId,
          entityId: row.id,
          moduleId: 3,
          comments: null,
        });
      } catch (error) {
        handleAPIError(error, toast, null);
      }
    }
  };

  const handleReconciliationCancel = () => {
    setReconciliationOpen(false);
    pendingReconcileResolve.current?.(false);
    pendingReconcileResolve.current = null;
    pendingReconcileStateId.current = null;
  };

  const columns = [];

  const handleInvoiceDetail = (invoiceID, jobId) => {
    navigate(`/invoicing/detail/${invoiceID}`);
  };
  const handleSave = () => {};

  columns.push(
    {
      field: 'date',
      headerName: intl.get('invoicesList.date'),
      width: 250,
      flex: 1,
      renderCell: (params) => <>{formatDateInvoices(params.row.date)}</>,
    },
    {
      field: 'number',
      headerName: intl.get('invoicesList.invoice.number'),
      width: 250,
      flex: 1,
    },
    {
      field: 'amount',
      headerName: intl.get('invoicesList.amount'),
      width: 250,
      flex: 1,
    },
    {
      field: 'status',
      headerName: intl.get('invoicesList.status'),
      width: 250,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
       <WorkflowStatusChip stateId={params.row.idWorkflowState} />
      ),
    }
  );

  columns.unshift({
    field: 'to',
    headerName: intl.get('newInvoice.to'),
    width: 250,
    flex: 1,
    renderCell: (params) => (
      <>{params.row.to === null || params.row.to === '' ? '-' : params.row.to}</>
    ),
  });

  if (!isValidating) {
    columns.push({
      field: 'id',
      headerName: intl.get('invoicesList.invoice.id'),
      width: 250,
      flex: 1,
    });

    columns.push({
      field: 'workflowActions',
      headerName: intl.get('invoicesList.actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <WorkflowActionsBar
          viewMode="icons"
          onActionClick={handleSave}
          id={params.row.id}
          stateId={params.row.idWorkflowState}
          loading={loading}
          onBeforeAction={handleBeforeAction(params.row)}
        />
      ),
    });
  }
  columns.push({
    field: 'actions',
    headerName: intl.get('invoicesList.detail'),
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" justifyContent="center">
        <IconButton onClick={() => handleInvoiceDetail(params.row.id, params.row.jobId)}>
          <VisibilityIcon />
        </IconButton>
      </Box>
    ),
  });

  return (
    <Box style={{ width: '100%', display: 'flex' }}>
      <InvoiceReconciliationDialog
        open={reconciliationOpen}
        invoiceId={reconciliationRow?.id ?? 0}
        invoiceAmount={Math.round((reconciliationRow?.amount ?? 0) * 100)}
        currency={reconciliationRow?.currency?.code ?? reconciliationRow?.currency ?? ''}
        onConfirm={handleReconciliationConfirm}
        onCancel={handleReconciliationCancel}
      />
      <WorkflowProvider moduleId={3}>
        <DataGrid
          rows={searchResult?.queryResult || []}
          getRowId={(row) => (row.id === 0 ? row.jobId : row.id)}
          columns={columns}
          loading={loading}
          rowCount={searchResult?.totalRecords || 0}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          pageSizeOptions={[5, 10, 20]}
          paginationMode="client"
          sortingMode="client"
        />
      </WorkflowProvider>
    </Box>
  );
};

export default InvoicingList;
